// §26.3 — the Connections app: a roster, one invite link, and two destructive buttons.
//
// **There is no directory and no search.** Accounts are invite-only (D-213), so the only way to
// reach somebody is a link they gave you — which means this app has exactly one "add" affordance
// and it produces a string to copy. A search box would imply a directory, and a directory of a
// population that fits in a text file is a way to enumerate everybody who uses this instance.
//
// Everything the app can do arrives as a `SocialShellAccess` (D-059). It reaches no kernel service
// and holds no `fetch` of its own — §6.5 forbids the first and §26.9 the second, because the roster
// is the one payload where "which app asked" has to be unambiguous.
//
// Presence is rendered **in words as well as in colour**: a dot alone is unreadable to anyone who
// cannot distinguish the two greens, and this is the one screen whose whole content is status.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  Connection,
  ConnectionState,
  MultiplayerGameId,
  Presence,
  PresenceState,
  SocialShellAccess,
} from '@hangar/shared';
import { COPY } from '@/copy';
import { LiveGames } from './LiveGames';
import { Standings } from './Standings';
import './connections.css';

export interface ConnectionsProps {
  access?: SocialShellAccess;
}

/** The four groups §26.3 names, in the order they are useful: what needs you, then what is. */
const GROUPS: readonly { state: ConnectionState; label: string }[] = [
  { state: 'pending_in', label: COPY.social.groupPendingIn },
  { state: 'accepted', label: COPY.social.groupAccepted },
  { state: 'pending_out', label: COPY.social.groupPendingOut },
  { state: 'blocked', label: COPY.social.groupBlocked },
];

/**
 * §26.2 (D-347) — the word for a state, for **every** state.
 *
 * This used to be `idle ? 'Idle' : 'Online'`, so `busy` — added at D-344 — came out as *Online*.
 * That is the wrong way round for this app in particular: §26.3 renders presence in words as well
 * as in colour precisely because a dot alone is unreadable to somebody who cannot distinguish the
 * two greens, so the word is the part that has to be right.
 *
 * A total `Record` rather than a chain of ternaries: the next state added to `PresenceState` fails
 * to compile here instead of quietly inheriting somebody else's label.
 */
const STATUS_LABELS: Readonly<Record<PresenceState, string>> = {
  online: COPY.social.online,
  idle: COPY.social.idle,
  busy: COPY.social.busy,
  offline: COPY.social.offline,
};

const statusLabel = (presence: Presence | undefined): string =>
  STATUS_LABELS[presence?.status ?? 'offline'];

/**
 * §26.7 (D-210) — the two games that can be suggested, and their names.
 *
 * The *list* comes from the shell (`access.playableGames()`), because which titles are playable
 * over the wire is the server's rule and this app must not hold a second copy of it. What is here
 * is the word for each, which is copy and belongs in `copy.ts` like all of it.
 */
const GAME_LABELS: Readonly<Record<MultiplayerGameId, string>> = {
  conquest: COPY.game.suggestConquest,
  intercept: COPY.game.suggestIntercept,
  chess: COPY.game.suggestChess,
  backgammon: COPY.game.suggestBackgammon,
  sortie: COPY.game.suggestSortie,
};

export function Connections({ access }: ConnectionsProps) {
  const [connections, setConnections] = useState<readonly Connection[]>([]);
  const [presence, setPresence] = useState<readonly Presence[]>([]);
  const [link, setLink] = useState<{ url: string; days: number } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const linkRef = useRef<HTMLInputElement | null>(null);

  const refresh = useCallback(async () => {
    if (access === undefined) return;
    // Both, always, and in parallel: a roster without presence renders every peer as offline for a
    // beat, which reads as "everyone left" rather than as "still loading".
    const [roster, live] = await Promise.all([
      access.connections().catch(() => [] as Connection[]),
      access.presence().catch(() => [] as Presence[]),
    ]);
    setConnections(roster);
    setPresence(live);
    setLoaded(true);
  }, [access]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // §26.6 — the doorbell says *that* something changed; this re-`GET`s. One refresh path, whether
  // the frame came over SSE or from F10's fallback poll.
  useEffect(() => {
    if (access === undefined) return;
    return access.subscribe(() => {
      void refresh();
    });
  }, [access, refresh]);

  const byPeer = useMemo(
    () => new Map(presence.map((entry) => [entry.peer, entry])),
    [presence],
  );

  const grouped = useMemo(
    () =>
      GROUPS.map((group) => ({
        ...group,
        rows: connections.filter((connection) => connection.state === group.state),
      })),
    [connections],
  );

  const invite = useCallback(async () => {
    if (access === undefined) return;
    const minted = await access.invite();
    const days = Math.max(1, Math.round((minted.expiresAt - Date.now()) / 86_400_000));
    setLink({ url: minted.link, days });
    setNotice(null);
  }, [access]);

  const copy = useCallback(async () => {
    if (link === null) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setNotice(COPY.social.copied);
    } catch {
      // A clipboard write can be refused by the browser with no warning. The field is selectable
      // and already focused, so the fallback is a real instruction rather than an apology.
      linkRef.current?.select();
      setNotice(COPY.social.copyFailed);
    }
  }, [link]);

  /**
   * §26.4 / §26.7 — suggest a game, which is one call and then somebody else's screen.
   *
   * The app does not open a window, draw a card or know what a play session is: `suggest` hands all
   * three to the shell (D-059). What it owns is the notice, because "asked them, waiting" is a fact
   * about *this* screen and there is nothing else on it that would say so.
   */
  const [suggesting, setSuggesting] = useState<string | null>(null);
  const suggest = useCallback(
    async (connection: Connection, gameId: MultiplayerGameId) => {
      if (access === undefined) return;
      setSuggesting(null);
      try {
        await access.suggest(gameId, connection.peer);
        setNotice(COPY.game.suggestSent(connection.displayName));
      } catch {
        setNotice(COPY.game.suggestFailed);
      }
    },
    [access],
  );

  const act = useCallback(
    async (connection: Connection, kind: 'remove' | 'block') => {
      if (access === undefined) return;
      // §19.4 — both confirm, through the shell. The message says what actually happens, because
      // "are you sure?" is not a description of a mutual, immediate removal.
      const message =
        kind === 'remove'
          ? COPY.social.confirmRemove(connection.displayName)
          : COPY.social.confirmBlock(connection.displayName);
      if (!(await access.confirm(message))) return;
      if (kind === 'remove') await access.remove(connection.peer);
      else await access.block(connection.peer);
      await refresh();
    },
    [access, refresh],
  );

  return (
    <div className="conn" data-testid="connections">
      <header className="conn-head">
        <h1 className="conn-title">{COPY.social.title}</h1>
        <button type="button" data-testid="conn-invite" onClick={() => void invite()}>
          {COPY.social.add}
        </button>
      </header>

      {link === null ? null : (
        <section className="conn-link" data-testid="conn-link">
          <label className="conn-link-label" htmlFor="conn-link-input">
            {COPY.social.linkLabel}
          </label>
          <div className="conn-link-row">
            <input
              id="conn-link-input"
              ref={linkRef}
              className="conn-link-input"
              data-testid="conn-link-input"
              readOnly
              value={link.url}
              onFocus={(event) => event.currentTarget.select()}
            />
            <button type="button" data-testid="conn-copy" onClick={() => void copy()}>
              {COPY.social.copy}
            </button>
          </div>
          <p className="conn-link-note">{COPY.social.linkExpires(link.days)}</p>
        </section>
      )}

      {notice === null ? null : (
        <p className="conn-notice" role="status" data-testid="conn-notice">
          {notice}
        </p>
      )}

      {loaded && connections.length === 0 ? (
        <p className="conn-empty" data-testid="conn-empty">
          {COPY.social.empty}
        </p>
      ) : null}

      {/* §26.8 (D-586) — what is unfinished, above what is merely who. A game waiting on your move
          is the one thing on this screen that is asking for something. */}
      <LiveGames {...(access === undefined ? {} : { access })} />

      {/* §30.6 (D-369) — Standings, inside the roster app rather than as a fourteenth icon. */}
      <Standings {...(access === undefined ? {} : { access })} connections={connections} />

      {grouped.map((group) =>
        group.rows.length === 0 ? null : (
          <section className="conn-group" key={group.state} data-testid={`conn-group-${group.state}`}>
            <h2 className="conn-group-title">{group.label}</h2>
            <ul className="conn-list">
              {group.rows.map((connection) => {
                const live = byPeer.get(connection.peer);
                const playing = live?.gameId ?? null;
                return (
                  <li
                    className="conn-row"
                    key={connection.peer}
                    data-testid={`conn-row-${connection.peer}`}
                  >
                    <span
                      className="presence-dot conn-dot"
                      data-status={live?.status ?? 'offline'}
                      data-testid={`conn-dot-${connection.peer}`}
                      aria-hidden="true"
                    />
                    <span className="conn-name">{connection.displayName}</span>
                    <span className="conn-status" data-testid={`conn-status-${connection.peer}`}>
                      {playing === null ? statusLabel(live) : COPY.social.playing(playing)}
                    </span>
                    <span className="conn-actions">
                      {/* §26.7 — only an accepted connection can be played with, because only an
                          accepted connection is somebody the server will start a session for. */}
                      {connection.state === 'accepted' ? (
                        <button
                          type="button"
                          data-testid={`conn-suggest-${connection.peer}`}
                          aria-expanded={suggesting === connection.peer}
                          onClick={() =>
                            setSuggesting((open) =>
                              open === connection.peer ? null : connection.peer,
                            )
                          }
                        >
                          {COPY.game.suggestGame}
                        </button>
                      ) : null}
                      {suggesting === connection.peer
                        ? (access?.playableGames() ?? []).map((gameId) => (
                            <button
                              type="button"
                              key={gameId}
                              data-testid={`conn-suggest-${gameId}-${connection.peer}`}
                              onClick={() => void suggest(connection, gameId)}
                            >
                              {GAME_LABELS[gameId]}
                            </button>
                          ))
                        : null}
                      {connection.state === 'blocked' ? null : (
                        <button
                          type="button"
                          data-testid={`conn-remove-${connection.peer}`}
                          onClick={() => void act(connection, 'remove')}
                        >
                          {COPY.social.remove}
                        </button>
                      )}
                      {connection.state === 'blocked' ? null : (
                        <button
                          type="button"
                          data-testid={`conn-block-${connection.peer}`}
                          onClick={() => void act(connection, 'block')}
                        >
                          {COPY.social.block}
                        </button>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ),
      )}
    </div>
  );
}

export default Connections;
